import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Sparkles, ArrowRight, ArrowLeft, Check, Loader2,
  FileText, Sliders, ListChecks, ChevronDown, ToggleLeft,
  CheckSquare, List, MessageSquare, Target, Users, Shield,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTION_TYPES = [
  { value: 'free_text', label: 'Free Text', icon: FileText, desc: 'Open-ended written response', color: '#5AA2AE' },
  { value: 'slider', label: 'Slider', icon: Sliders, desc: 'Numeric scale (0–10)', color: '#8b5cf6' },
  { value: 'multiple_choice', label: 'Multiple Choice', icon: ListChecks, desc: 'Pick one from a list', color: '#f59e0b' },
  { value: 'dropdown', label: 'Dropdown', icon: ChevronDown, desc: 'Select from a dropdown', color: '#06b6d4' },
  { value: 'yes_no', label: 'Yes / No', icon: ToggleLeft, desc: 'Simple binary choice', color: '#22c55e' },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare, desc: 'Select multiple options', color: '#ec4899' },
  { value: 'multi_topic', label: 'Multi-Topic', icon: List, desc: 'Ranked list of topics', color: '#f97316' },
];

const defaultSliderConfig = {
  left_label: 'Low', middle_label: 'Medium', right_label: 'High',
  min_value: 0, max_value: 10, left_color: null, middle_color: null, right_color: null,
};

export default function AddQuestionWizard({ open, onClose, onCreated, projectId, drivers, segments, activeContext, token }) {
  const [step, setStep] = useState(1);
  const [questionText, setQuestionText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);

  // Form state built from wizard
  const [heading, setHeading] = useState('');
  const [selectedType, setSelectedType] = useState('free_text');
  const [driverId, setDriverId] = useState('');
  const [correctedText, setCorrectedText] = useState('');
  const [options, setOptions] = useState(['']);
  const [sliderConfig, setSliderConfig] = useState(defaultSliderConfig);
  const [segmentIds, setSegmentIds] = useState([]);
  const [required, setRequired] = useState(true);
  const [allowSkip, setAllowSkip] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const totalSteps = 4;

  const reset = () => {
    setStep(1);
    setQuestionText('');
    setAiLoading(false);
    setAiSuggestion(null);
    setHeading('');
    setSelectedType('free_text');
    setDriverId('');
    setCorrectedText('');
    setOptions(['']);
    setSliderConfig(defaultSliderConfig);
    setSegmentIds([]);
    setRequired(true);
    setAllowSkip(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Step 1 -> 2: Call AI
  const handleAnalyze = async () => {
    if (!questionText.trim()) { toast.error('Please enter a question'); return; }
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/questions/ai-suggest`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ question_text: questionText, context: activeContext }),
      });
      if (!res.ok) throw new Error('AI suggestion failed');
      const data = await res.json();
      setAiSuggestion(data);
      setCorrectedText(data.corrected_text || questionText);
      setHeading(data.heading || '');
      setSelectedType(data.question_type || 'free_text');
      setDriverId(data.suggested_driver_id || '');
      if (data.options?.length > 0) setOptions(data.options);
      if (data.slider_labels) {
        setSliderConfig(prev => ({
          ...prev,
          left_label: data.slider_labels.left || 'Low',
          middle_label: data.slider_labels.middle || 'Medium',
          right_label: data.slider_labels.right || 'High',
        }));
      }
      setStep(2);
    } catch {
      toast.error('AI analysis failed. You can still configure manually.');
      setCorrectedText(questionText);
      setStep(2);
    } finally {
      setAiLoading(false);
    }
  };

  // Step 4: Create question
  const handleCreate = async () => {
    if (!heading.trim()) { toast.error('Please enter a heading'); return; }
    if (!correctedText.trim()) { toast.error('Please enter question text'); return; }
    if (activeContext === 'about_me' && !driverId) { toast.error('Please select a driver'); return; }

    setSubmitting(true);
    try {
      const payload = {
        text: correctedText,
        heading,
        question_type: selectedType,
        question_context: activeContext,
        driver_id: activeContext === 'about_me' ? driverId : null,
        segment_ids: segmentIds,
        required,
        allow_skip: allowSkip,
      };
      if (['multiple_choice', 'dropdown', 'checkbox'].includes(selectedType)) {
        payload.options = options.filter(o => o.trim());
      }
      if (selectedType === 'slider') {
        payload.slider_config = sliderConfig;
      }

      const res = await fetch(`${API_URL}/api/projects/${projectId}/questions`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Failed'); }
      toast.success('Question created');
      handleClose();
      onCreated?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const addOption = () => setOptions(p => [...p, '']);
  const updateOption = (i, v) => setOptions(p => { const n = [...p]; n[i] = v; return n; });
  const removeOption = (i) => setOptions(p => p.length > 1 ? p.filter((_, idx) => idx !== i) : p);

  const toggleSegment = (id) => {
    setSegmentIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  // Progress bar
  const ProgressDots = () => (
    <div className="flex items-center gap-1.5 justify-center mb-6">
      {Array.from({ length: totalSteps }, (_, i) => (
        <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${
          i + 1 === step ? 'w-8 bg-[#5AA2AE]' : i + 1 < step ? 'w-4 bg-[#5AA2AE]/40' : 'w-4 bg-slate-200'
        }`} />
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" data-testid="add-question-wizard">
        {/* Header accent */}
        <div className="h-1 bg-gradient-to-r from-[#5AA2AE] via-[#5AA2AE]/60 to-transparent" />

        <div className="px-6 pt-5 pb-6">
          <ProgressDots />

          {/* ========= STEP 1: Enter Question ========= */}
          {step === 1 && (
            <div className="space-y-5" data-testid="wizard-step-1">
              <div className="text-center">
                <div className="w-12 h-12 rounded-2xl bg-[#5AA2AE]/10 flex items-center justify-center mx-auto mb-3">
                  <MessageSquare className="w-6 h-6 text-[#5AA2AE]" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">What do you want to ask?</h2>
                <p className="text-sm text-slate-500 mt-1">Type your question and we'll help you set it up.</p>
              </div>

              <Textarea
                autoFocus
                placeholder="e.g. How confident are you that the project will be delivered on time?"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                rows={3}
                className="text-base resize-none"
                data-testid="wizard-question-input"
              />

              <Button
                className="w-full bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 gap-2"
                onClick={handleAnalyze}
                disabled={!questionText.trim() || aiLoading}
                data-testid="wizard-analyze-btn"
              >
                {aiLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Analyze &amp; Suggest</>
                )}
              </Button>
            </div>
          )}

          {/* ========= STEP 2: AI Suggestions + Type Selection ========= */}
          {step === 2 && (
            <div className="space-y-5" data-testid="wizard-step-2">
              <div className="text-center mb-2">
                <h2 className="text-lg font-bold text-slate-900">Review &amp; Choose Type</h2>
                <p className="text-sm text-slate-500 mt-1">AI suggestions — adjust anything you'd like.</p>
              </div>

              {/* Corrected text */}
              {correctedText !== questionText && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs font-medium text-amber-700 mb-1">Suggested revision:</p>
                  <p className="text-sm text-amber-900">{correctedText}</p>
                </div>
              )}

              {/* Heading + Driver */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500">Heading</Label>
                  <Input
                    value={heading}
                    onChange={(e) => setHeading(e.target.value)}
                    placeholder="e.g. Schedule Confidence"
                    data-testid="wizard-heading-input"
                  />
                </div>
                {activeContext === 'about_me' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Driver</Label>
                    <Select value={driverId} onValueChange={setDriverId}>
                      <SelectTrigger data-testid="wizard-driver-select">
                        <SelectValue placeholder="Select driver" />
                      </SelectTrigger>
                      <SelectContent>
                        {drivers.map(d => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {aiSuggestion?.suggested_driver_name && (
                      <p className="text-[10px] text-[#5AA2AE]">
                        AI suggested: {aiSuggestion.suggested_driver_name}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Question type visual picker */}
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">Question Type</Label>
                <div className="grid grid-cols-2 gap-2">
                  {QUESTION_TYPES.map((t) => {
                    const Icon = t.icon;
                    const isSelected = selectedType === t.value;
                    const isAiPick = aiSuggestion?.question_type === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setSelectedType(t.value)}
                        className={`relative flex items-center gap-2.5 p-2.5 rounded-xl border-2 text-left transition-all ${
                          isSelected
                            ? 'border-[#5AA2AE] bg-[#5AA2AE]/5 shadow-sm'
                            : 'border-slate-150 hover:border-slate-300 bg-white'
                        }`}
                        data-testid={`wizard-type-${t.value}`}
                      >
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${t.color}15` }}>
                          <Icon className="w-4 h-4" style={{ color: t.color }} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-slate-800 leading-tight">{t.label}</p>
                          <p className="text-[10px] text-slate-400 leading-tight truncate">{t.desc}</p>
                        </div>
                        {isAiPick && !isSelected && (
                          <Badge className="absolute -top-1.5 -right-1.5 text-[8px] px-1.5 py-0 bg-[#5AA2AE] text-white">AI</Badge>
                        )}
                        {isSelected && (
                          <Check className="w-4 h-4 text-[#5AA2AE] absolute top-1.5 right-1.5" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setStep(1)} className="gap-1">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </Button>
                <Button size="sm" onClick={() => setStep(3)} className="flex-1 bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 gap-1"
                  data-testid="wizard-step2-next">
                  Next <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}

          {/* ========= STEP 3: Type-Specific Options + Segments ========= */}
          {step === 3 && (
            <div className="space-y-5" data-testid="wizard-step-3">
              <div className="text-center mb-2">
                <h2 className="text-lg font-bold text-slate-900">Configure Options</h2>
                <p className="text-sm text-slate-500 mt-1">Set up type-specific settings and audience.</p>
              </div>

              {/* Options for MC / Dropdown / Checkbox */}
              {['multiple_choice', 'dropdown', 'checkbox'].includes(selectedType) && (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500 flex items-center gap-1">
                    <ListChecks className="w-3.5 h-3.5" /> Answer Options
                  </Label>
                  <div className="space-y-1.5">
                    {options.map((opt, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <span className="w-5 text-xs text-slate-400 text-right">{i + 1}.</span>
                        <Input
                          placeholder={`Option ${i + 1}`}
                          value={opt}
                          onChange={(e) => updateOption(i, e.target.value)}
                          className="h-8 text-sm"
                          data-testid={`wizard-option-${i}`}
                        />
                        {options.length > 1 && (
                          <button type="button" onClick={() => removeOption(i)}
                            className="text-slate-300 hover:text-red-400 text-xs px-1">✕</button>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={addOption}
                    className="text-xs text-[#5AA2AE] hover:text-[#5AA2AE]/80 h-7">
                    + Add Option
                  </Button>
                </div>
              )}

              {/* Slider Labels */}
              {selectedType === 'slider' && (
                <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                  <Label className="text-xs text-slate-500 flex items-center gap-1">
                    <Sliders className="w-3.5 h-3.5" /> Slider Labels
                  </Label>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <Label className="text-[10px] text-slate-400">Left</Label>
                      <Input className="h-8 text-sm" value={sliderConfig.left_label}
                        onChange={(e) => setSliderConfig(p => ({ ...p, left_label: e.target.value }))}
                        data-testid="wizard-slider-left" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-slate-400">Middle</Label>
                      <Input className="h-8 text-sm" value={sliderConfig.middle_label}
                        onChange={(e) => setSliderConfig(p => ({ ...p, middle_label: e.target.value }))}
                        data-testid="wizard-slider-middle" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-slate-400">Right</Label>
                      <Input className="h-8 text-sm" value={sliderConfig.right_label}
                        onChange={(e) => setSliderConfig(p => ({ ...p, right_label: e.target.value }))}
                        data-testid="wizard-slider-right" />
                    </div>
                  </div>
                  {/* Scale range */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-[10px] text-slate-400">Min</Label>
                      <Input type="number" className="h-8 text-sm" value={sliderConfig.min_value}
                        onChange={(e) => setSliderConfig(p => ({ ...p, min_value: parseInt(e.target.value) || 0 }))} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-slate-400">Max</Label>
                      <Input type="number" className="h-8 text-sm" value={sliderConfig.max_value}
                        onChange={(e) => setSliderConfig(p => ({ ...p, max_value: parseInt(e.target.value) || 10 }))} />
                    </div>
                  </div>
                </div>
              )}

              {/* Yes/No, Free Text, Multi-Topic — no extra config needed */}
              {['yes_no', 'free_text', 'multi_topic'].includes(selectedType) && (
                <div className="p-3 bg-slate-50 rounded-lg text-center text-sm text-slate-500">
                  No additional options needed for {QUESTION_TYPES.find(t => t.value === selectedType)?.label}.
                </div>
              )}

              {/* Segment Targeting */}
              {segments.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500 flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" /> Who should see this question?
                  </Label>
                  <div className="flex items-center gap-2 mb-1.5">
                    <button type="button"
                      onClick={() => setSegmentIds([])}
                      className={`text-xs px-3 py-1 rounded-full border transition-all ${
                        segmentIds.length === 0 ? 'bg-[#5AA2AE] text-white border-[#5AA2AE]' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                      data-testid="wizard-segments-all"
                    >All Users</button>
                    <span className="text-xs text-slate-400">or narrow down:</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {segments.map(seg => (
                      <button key={seg.id} type="button"
                        onClick={() => toggleSegment(seg.id)}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                          segmentIds.includes(seg.id) ? 'bg-[#5AA2AE]/10 border-[#5AA2AE] text-[#5AA2AE]' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                        data-testid={`wizard-segment-${seg.id}`}
                      >{seg.name}</button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setStep(2)} className="gap-1">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </Button>
                <Button size="sm" onClick={() => setStep(4)} className="flex-1 bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 gap-1"
                  data-testid="wizard-step3-next">
                  Next <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}

          {/* ========= STEP 4: Required / Skip + Confirmation ========= */}
          {step === 4 && (
            <div className="space-y-5" data-testid="wizard-step-4">
              <div className="text-center mb-2">
                <h2 className="text-lg font-bold text-slate-900">Final Settings</h2>
                <p className="text-sm text-slate-500 mt-1">Review and create your question.</p>
              </div>

              {/* Summary card */}
              <div className="p-4 bg-slate-50 rounded-xl space-y-2.5 border border-slate-100">
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#5AA2AE]/10 text-[#5AA2AE] text-[10px] border-0">{heading || 'No heading'}</Badge>
                  <Badge variant="outline" className="text-[10px]">
                    {QUESTION_TYPES.find(t => t.value === selectedType)?.label}
                  </Badge>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{correctedText}</p>
                {activeContext === 'about_me' && driverId && (
                  <p className="text-xs text-slate-400">
                    Driver: {drivers.find(d => d.id === driverId)?.name || 'Unknown'}
                  </p>
                )}
                {segmentIds.length > 0 && (
                  <p className="text-xs text-slate-400">
                    Segments: {segmentIds.map(id => segments.find(s => s.id === id)?.name).filter(Boolean).join(', ')}
                  </p>
                )}
              </div>

              {/* Required / Skip toggles */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-slate-400" />
                    <Label htmlFor="wiz-required" className="text-sm">Required question</Label>
                  </div>
                  <Switch id="wiz-required" checked={required}
                    onCheckedChange={setRequired} data-testid="wizard-required-toggle" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-slate-400" />
                      <Label htmlFor="wiz-skip" className="text-sm">Allow skip</Label>
                    </div>
                    <p className="text-xs text-slate-400 ml-6">Users can skip with a reason</p>
                  </div>
                  <Switch id="wiz-skip" checked={allowSkip}
                    onCheckedChange={setAllowSkip} data-testid="wizard-skip-toggle" />
                </div>
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setStep(3)} className="gap-1">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </Button>
                <Button size="sm" onClick={handleCreate}
                  disabled={submitting || !heading.trim() || !correctedText.trim()}
                  className="flex-1 bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 gap-1"
                  data-testid="wizard-create-btn">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</>
                    : <><Check className="w-4 h-4" /> Create Question</>}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
